import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown, Activity, AlertCircle } from 'lucide-react';

const SignalPanel = ({ signals }) => {
  const getSignalColor = (signal) => {
    if (signal === 'BUY') return 'bg-emerald-500';
    if (signal === 'SELL') return 'bg-rose-500';
    return 'bg-slate-500';
  };

  const getSignalIcon = (signal) => {
    if (signal === 'BUY') return <TrendingUp className="w-4 h-4" />;
    if (signal === 'SELL') return <TrendingDown className="w-4 h-4" />;
    return <Activity className="w-4 h-4" />;
  };

  return (
    <Card className="signal-panel" data-testid="signal-panel-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="w-5 h-5" />
          Trading Signals
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Main Signal */}
        <div className="main-signal" data-testid="main-signal">
          <div className={`signal-badge ${getSignalColor(signals.signal)}`} data-testid="signal-badge">
            {getSignalIcon(signals.signal)}
            <span className="signal-text" data-testid="signal-text">{signals.signal}</span>
          </div>
          <p className="signal-reason" data-testid="signal-reason">{signals.reason}</p>
        </div>

        {/* Indicator Breakdown */}
        {signals.indicators && (
          <div className="indicators-breakdown" data-testid="indicators-breakdown">
            <h4 className="indicators-title">Indicators</h4>
            
            {/* MACD */}
            <div className="indicator-item" data-testid="macd-indicator">
              <div className="indicator-header">
                <span className="indicator-name">MACD (5,25,5)</span>
                <Badge 
                  variant={signals.indicators.macd.signal === 'BUY' ? 'default' : signals.indicators.macd.signal === 'SELL' ? 'destructive' : 'secondary'}
                  data-testid="macd-signal-badge"
                >
                  {signals.indicators.macd.signal}
                </Badge>
              </div>
              <p className="indicator-value" data-testid="macd-value">Histogram: {signals.indicators.macd.value.toFixed(4)}</p>
            </div>

            {/* Fractal Chaos Bands */}
            <div className="indicator-item" data-testid="fractal-indicator">
              <div className="indicator-header">
                <span className="indicator-name">Fractal Chaos Bands (3)</span>
                <Badge 
                  variant={signals.indicators.fractal.signal === 'BUY' ? 'default' : signals.indicators.fractal.signal === 'SELL' ? 'destructive' : 'secondary'}
                  data-testid="fractal-signal-badge"
                >
                  {signals.indicators.fractal.signal}
                </Badge>
              </div>
              <div className="fractal-values" data-testid="fractal-values">
                <span>Upper: {signals.indicators.fractal.upper.toFixed(4)}</span>
                <span>Lower: {signals.indicators.fractal.lower.toFixed(4)}</span>
              </div>
            </div>

            {/* Moving Average */}
            <div className="indicator-item" data-testid="ma-indicator">
              <div className="indicator-header">
                <span className="indicator-name">7-Period MA</span>
                <Badge 
                  variant={signals.indicators.ma.signal === 'BUY' ? 'default' : signals.indicators.ma.signal === 'SELL' ? 'destructive' : 'secondary'}
                  data-testid="ma-signal-badge"
                >
                  {signals.indicators.ma.signal}
                </Badge>
              </div>
              <p className="indicator-value" data-testid="ma-value">Value: {signals.indicators.ma.value.toFixed(4)}</p>
            </div>

            {/* Volume Spike */}
            <div className="indicator-item" data-testid="volume-indicator">
              <div className="indicator-header">
                <span className="indicator-name">Volume Spike</span>
                <Badge variant={signals.indicators.volume_spike ? 'default' : 'secondary'} data-testid="volume-spike-badge">
                  {signals.indicators.volume_spike ? 'YES' : 'NO'}
                </Badge>
              </div>
            </div>
          </div>
        )}

        {/* Signal Count */}
        <div className="signal-count" data-testid="signal-count">
          <div className="count-item">
            <span className="count-label">Buy Signals:</span>
            <span className="count-value buy" data-testid="buy-count">{signals.buy_count || 0}/3</span>
          </div>
          <div className="count-item">
            <span className="count-label">Sell Signals:</span>
            <span className="count-value sell" data-testid="sell-count">{signals.sell_count || 0}/3</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SignalPanel;
